/**
 * API Routes - Centralized route definitions
 *
 * Single source of truth for all API endpoints
 * Use these constants instead of hardcoded strings
 *
 * Usage:
 *   import { API_ROUTES } from "@/frontend/lib/api/routes";
 *   fetch(API_ROUTES.workspaces.getById(id), ...)
 */

export const API_ROUTES = {
  // ============================================================================
  // WORKSPACES
  // ============================================================================
  workspaces: {
    getAll: "/api/workspace/all/get",
    getById: (id: string) => `/api/workspace/${id}/get`,
    dashboard: (id: string) => `/api/workspaces/${id}/dashboard`,
    dashboardCallSeries: (id: string) => `/api/workspaces/${id}/dashboard/call-series`,
    dashboardCallMinutes: (id: string) => `/api/workspaces/${id}/dashboard/call-minutes`,
    create: "/api/workspace/save",
    update: (id: string) => `/api/workspace/${id}/save`,
    archive: (id: string) => `/api/workspace/${id}/archive`,
  },

  // ============================================================================
  // WORKFLOWS (PROTOCOLS)
  // ============================================================================
  workflows: {
    getAll: "/api/workflows/get",
    getById: (id: string) => `/api/workflows/${id}/get`,
    getWithPhases: (id: string) => `/api/workflows/${id}/with-phases`,
    getByAgent: (agentId: string) => `/api/workflows/byAgent/${agentId}`,
    getByAgentWithPhases: (agentId: string) => `/api/workflows/byAgent/${agentId}/with-phases`,
    getByWorkspace: (workspaceId: string) => `/api/workflows/byWorkspace/${workspaceId}`,
    create: "/api/workflows/create",
    update: (id: string) => `/api/workflows/${id}/update`,
    save: (id: string) => `/api/workflows/${id}/save`,
    delete: (id: string) => `/api/workflows/${id}/delete`,
  },

  // ============================================================================
  // PHASES
  // ============================================================================
  phases: {
    getById: (id: string) => `/api/phases/${id}/get`,
    getByWorkflow: (workflowId: string) => `/api/phases/byWorkflow/${workflowId}`,
    create: "/api/phases/create",
    update: (id: string) => `/api/phases/${id}/update`,
    delete: (id: string) => `/api/phases/${id}/delete`,
    linkKb: (id: string) => `/api/phases/${id}/link-kb`,
    unlinkKb: (id: string) => `/api/phases/${id}/unlink-kb`,
  },

  // ============================================================================
  // PATIENTS
  // ============================================================================
  patients: {
    getById: (id: string) => `/api/patients/${id}/get`,
    getByWorkspace: (workspaceId: string) => `/api/patients/byWorkspace/${workspaceId}`,
    create: "/api/patients/create",
    update: (id: string) => `/api/patients/${id}/update`,
  },

  // ============================================================================
  // PATIENT MEDICAL PROBLEMS
  // ============================================================================
  patientMedicalProblems: {
    getByPatient: (patientId: string) => `/api/patients/${patientId}/medical-problems`,
    create: (patientId: string) => `/api/patients/${patientId}/medical-problems`,
    update: (problemId: string) => `/api/patients/medical-problems/${problemId}`,
    delete: (problemId: string) => `/api/patients/medical-problems/${problemId}`,
  },

  // ============================================================================
  // AGENTS
  // ============================================================================
  agents: {
    getById: (id: string) => `/api/agents/${id}/get`,
    getByWorkspace: (workspaceId: string) => `/api/agents/byWorkspace/${workspaceId}`,
    getGroupsByWorkspace: (workspaceId: string) => `/api/agents/groups/byWorkspace/${workspaceId}`,
    defaultFromNumber: "/api/agents/default-from-number",
    create: "/api/agents/create",
    update: (id: string) => `/api/agents/${id}/save`,
    delete: (id: string) => `/api/agents/${id}/delete`,
    archive: (id: string) => `/api/agents/${id}/archive`,
    restore: (id: string) => `/api/agents/${id}/restore`,
    release: (id: string) => `/api/agents/${id}/release`,
    deploy: (id: string) => `/api/agents/${id}/deploy`,
    undeploy: (id: string) => `/api/agents/${id}/undeploy`,
    activate: (id: string) => `/api/agents/${id}/activate`,
    deactivate: (id: string) => `/api/agents/${id}/deactivate`,
    redeploy: (id: string) => `/api/agents/${id}/redeploy`,
    versions: (id: string) => `/api/agents/${id}/versions`,
    publish: (id: string) => `/api/agents/${id}/publish`,
    testPhoneCall: (id: string) => `/api/agents/${id}/test-phone-call`,
    testWebCall: (id: string) => `/api/agents/${id}/test-web-call`,
    testCall: (testCallId: string) => `/api/agents/tests/${testCallId}`,
    tests: (agentId: string) => `/api/agents/${agentId}/tests`,
    testCalls: {
      refresh: (testCallId: string) => `/api/agents/test-calls/${testCallId}/refresh`,
    },
  },

  // ============================================================================
  // KNOWLEDGE BASES
  // ============================================================================
  knowledgeBases: {
    getById: (id: string) => `/api/knowledge-bases/${id}/get`,
    getByWorkspace: (workspaceId: string) => `/api/knowledge-bases/byWorkspace/${workspaceId}`,
    create: "/api/knowledge-bases/create",
    update: (id: string) => `/api/knowledge-bases/${id}/update`,
    delete: (id: string) => `/api/knowledge-bases/${id}/delete`,
    archive: (id: string) => `/api/knowledge-bases/${id}/archive`,
    uploadFile: (id: string) => `/api/knowledge-bases/${id}/upload-file`,
  },

  // ============================================================================
  // FOLLOW-UPS
  // ============================================================================
  followUps: {
    getById: (id: string) => `/api/follow-ups/${id}/get`,
    getByPatient: (patientId: string) => `/api/follow-ups/byPatient/${patientId}`,
    list: "/api/follow-ups/list",
    create: "/api/follow-ups/create",
    update: (id: string) => `/api/follow-ups/${id}/update`,
  },

  // ============================================================================
  // CALL ATTEMPTS
  // ============================================================================
  callAttempts: {
    getById: (id: string) => `/api/call-attempts/${id}/get`,
    getBySession: (sessionId: string) => `/api/call-attempts/bySession/${sessionId}`,
    review: (id: string) => `/api/call-attempts/${id}/review`,
  },

  // ============================================================================
  // CALL SESSIONS
  // ============================================================================
  callSessions: {
    getById: (id: string) => `/api/call-sessions/${id}/get`,
    getByFollowUp: (followUpId: string) => `/api/call-sessions/byFollowUp/${followUpId}`,
  },

  // ============================================================================
  // TRIGGERED ALERTS
  // ============================================================================
  triggeredAlerts: {
    getByFollowUp: (followUpId: string) => `/api/triggered-alerts/byFollowUp/${followUpId}`,
    getUnresolvedByWorkspace: (workspaceId: string) => `/api/triggered-alerts/unresolved/${workspaceId}`,
  },

  // ============================================================================
  // AUDIT LOGS
  // ============================================================================
  auditLogs: {
    getByWorkspace: (workspaceId: string) => `/api/audit-logs/byWorkspace/${workspaceId}`,
  },

  // ============================================================================
  // PROFILE
  // ============================================================================
  profile: {
    get: "/api/profile",
    create: "/api/profile/create",
    update: "/api/profile",
    getAll: "/api/profile/get/all",
  },

  // ============================================================================
  // USER
  // ============================================================================
  user: {
    getWorkspaces: "/api/user/workspaces",
    getRole: "/api/user/role",
  },

  // ============================================================================
  // ADMIN
  // ============================================================================
  admin: {
    profiles: "/api/admin/profiles",
    workspaces: {
      getAll: "/api/admin/workspaces",
      getMembers: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/members`,
      addMember: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/members/add`,
      updateMemberRole: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/members`,
      invitations: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/invitations`,
      revokeInvitation: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/invitations/revoke`,
      resendInvitation: (workspaceId: string) => `/api/admin/workspaces/${workspaceId}/invitations/resend`,
    },
    testCalls: {
      getAll: "/api/admin/test-calls",
      batchRefresh: "/api/admin/test-calls/refresh",
    },
  },

  // ============================================================================
  // AUTH
  // ============================================================================
  auth: {
    acceptInvitation: "/api/auth/invitations/accept",
  },

  // ============================================================================
  // AI
  // ============================================================================
  ai: {
    chat: "/api/ai/chat",
    generateWorkflow: "/api/ai/generate-workflow",
    extractQuestionnaire: "/api/ai/extract-questionnaire",
    suggestQuestion: "/api/ai/suggest-question",
  },

  // ============================================================================
  // STORE
  // ============================================================================
  store: {
    listings: {
      getAll: "/api/store/listings",
      getById: (id: string) => `/api/store/listings/${id}`,
    },
    myResources: "/api/store/my-resources",
    publish: "/api/store/publish",
    licenses: {
      purchase: "/api/store/licenses/purchase",
      myLicenses: "/api/store/licenses/my-licenses",
      importVersion: (licenseId: string) => `/api/store/licenses/${licenseId}/import-version`,
    },
    buyer: {
      updates: "/api/store/buyer/updates",
    },
    reviews: {
      create: "/api/store/reviews",
    },
  },
} as const;

/**
 * Type helper to ensure route functions are used correctly
 */
export type ApiRoute = string | ((param: string) => string);
